// Project Type Taxonomy System
// Kategorien der Rückwärtsplanung nach taxonomischer Klassifikation

export enum ProjectPlanningCategory {
  LEARNING_GOAL = 'learning_goal',           // Backward Design (Didaktik)
  PROJECT_GOAL = 'project_goal',             // Backward Scheduling (Meilensteine)
  PERSONAL_GOAL = 'personal_goal',           // Goal Breakdown
  VISION_GOAL = 'vision_goal',               // Backcasting
  TECHNICAL_GOAL = 'technical_goal'          // Reverse Planning/Engineering
}

// Zentrale Projekttyp-Definitionen
export interface ProjectTypeDefinition {
  category: ProjectPlanningCategory;
  label: string;
  description: string;
  characteristics: {
    triggerType: 'deadline' | 'outcome' | 'milestone' | 'vision' | 'result';
    timeHorizon: 'short' | 'medium' | 'long' | 'indefinite';
    measurementFocus: 'completion' | 'performance' | 'learning' | 'transformation' | 'functionality';
  };
  planningSteps: PlanningStep[];
  requiredFields: ProjectTypeField[];
  artifactTypes: ArtifactType[];
  validationRules: ValidationRule[];
  aiPromptTemplate: string;
}

// Planungsschritte je nach Typ
export interface PlanningStep {
  order: number;
  name: string;
  description: string;
  isRequired: boolean;
  defaultDuration?: number; // in days
}

// Typspezifische Felder
export interface ProjectTypeField {
  key: string;
  label: string;
  type: 'text' | 'date' | 'number' | 'select' | 'multi-select' | 'json';
  required: boolean;
  options?: string[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
}

// Artefakt-Typen
export interface ArtifactType {
  name: string;
  description: string;
  isRequired: boolean;
  template?: any;
}

// Validierungsregeln
export interface ValidationRule {
  field: string;
  rule: 'required' | 'min' | 'max' | 'date_future' | 'date_past' | 'depends_on';
  value?: any;
  dependsOn?: string;
  errorMessage: string;
}

// Erweiterte Project-Interface mit Taxonomie
export interface ProjectWithType {
  id: string;
  name: string;
  description: string;
  planningCategory: ProjectPlanningCategory;
  typeSpecificData: Record<string, any>;
  planningMetadata: {
    triggerType: string;
    timeHorizon: string;
    measurementFocus: string;
    steps: CompletedPlanningStep[];
    artifacts: ProjectArtifact[];
  };
  // Existing fields...
  status: 'planning' | 'active' | 'completed' | 'on_hold';
  techStack?: string[];
  architecture?: string;
  existingAssets?: string[];
  totalBudget?: number;
  targetEndDate?: string;
  aiGenerated: boolean;
  expertProfile?: any;
  createdAt: string;
}

// Abgeschlossene Planungsschritte
export interface CompletedPlanningStep {
  stepName: string;
  completedAt: string;
  data: Record<string, any>;
  notes?: string;
}

// Projekt-Artefakte
export interface ProjectArtifact {
  id: string;
  type: string;
  name: string;
  data: any;
  createdAt: string;
  updatedAt?: string;
}

// Projekttyp-spezifische Goal-Erweiterungen
export interface TypedGoal {
  // Standard Goal properties
  id: string;
  projectId: string;
  name: string;
  description: string;
  
  // Type-specific extensions
  goalType?: string;
  typeSpecificData?: {
    // Für learning_goal
    learningOutcome?: string;
    assessmentMethod?: string;
    competencyLevel?: number;
    
    // Für project_goal
    milestone?: boolean;
    dependencies?: string[];
    deliverables?: string[];
    
    // Für personal_goal
    routine?: boolean;
    measurementPoint?: string;
    etappeGoal?: boolean;
    
    // Für vision_goal
    constraint?: string[];
    scenarios?: string[];
    keyInitiative?: boolean;
    
    // Für technical_goal
    contract?: string;
    prototype?: boolean;
    techRisk?: string;
    sequenceDiagram?: string;
  };
}

// Projekttyp-Empfehlungen
export interface ProjectTypeRecommendation {
  category: ProjectPlanningCategory;
  confidence: number;
  reasoning: {
    keywords: string[];
    patterns: string[];
    indicators: string[];
  };
  explanation: string;
}

// Projekttyp-Wizard State
export interface ProjectTypeWizardState {
  currentStep: number;
  totalSteps: number;
  selectedCategory?: ProjectPlanningCategory;
  formData: Record<string, any>;
  completedSteps: string[];
  validationErrors: Record<string, string | undefined>;
  suggestedCategory?: ProjectPlanningCategory;
  confidence?: number;
  recommendations?: ProjectTypeRecommendation[];
}

// Type Guard Functions
export const isLearningGoal = (category: ProjectPlanningCategory): boolean => 
  category === ProjectPlanningCategory.LEARNING_GOAL;

export const isProjectGoal = (category: ProjectPlanningCategory): boolean => 
  category === ProjectPlanningCategory.PROJECT_GOAL;

export const isPersonalGoal = (category: ProjectPlanningCategory): boolean => 
  category === ProjectPlanningCategory.PERSONAL_GOAL;

export const isVisionGoal = (category: ProjectPlanningCategory): boolean => 
  category === ProjectPlanningCategory.VISION_GOAL;

export const isTechnicalGoal = (category: ProjectPlanningCategory): boolean => 
  category === ProjectPlanningCategory.TECHNICAL_GOAL;